import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import StatusPage from './pages/StatusPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import PricingPage from './pages/PricingPage';
import OrderPage from './pages/OrderPage';
import SuccessPage from './pages/SuccessPage';
import CancelPage from './pages/CancelPage';
import ConsolePage from './pages/ConsolePage';
import BillingPage from './pages/BillingPage';
import ServerManagePage from './pages/ServerManagePage';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import FeaturesPage from './pages/FeaturesPage';
import StaffPage from './pages/StaffPage';
import DocsPage from './pages/DocsPage';
import BalancePage from './pages/BalancePage';
import AccountPage from './pages/AccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import './index.css';


// Site-wide announcement banner (managed from the admin panel)
const AnnouncementBanner = () => {
  const [ann, setAnn] = React.useState(null);
  React.useEffect(() => {
    fetch('/api/announcements/active')
      .then(r => r.json())
      .then(d => {
        if (d.announcement && localStorage.getItem(`ann_dismissed_${d.announcement.id}`) !== '1') {
          setAnn(d.announcement);
        }
      })
      .catch(() => {});
  }, []);
  if (!ann) return null;
  const colors = {
    info:    'bg-blue-500/10 border-blue-500/30 text-blue-300',
    warning: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300',
    success: 'bg-green-500/10 border-green-500/30 text-green-300',
  };
  return (
    <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-lg w-[calc(100%-2rem)] backdrop-blur border rounded-xl px-4 py-3 text-sm flex items-start gap-3 shadow-2xl ${colors[ann.type] || colors.info}`}>
      <span className="flex-1">{ann.message}</span>
      <button
        onClick={() => { localStorage.setItem(`ann_dismissed_${ann.id}`, '1'); setAnn(null); }}
        className="opacity-60 hover:opacity-100 font-bold px-1"
        aria-label="Dismiss"
      >✕</button>
    </div>
  );
};

function App() {
  // Serve status page on status subdomain
  if (window.location.hostname === 'status.plarixmc.com') {
    return (
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          <Route path="*" element={<StatusPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AnnouncementBanner />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#1f2937',
              color: '#f9fafb',
              border: '1px solid #374151',
            },
            success: {
              duration: 4000,
              iconTheme: { primary: '#22c55e', secondary: '#111827' },
            },
            error: {
              duration: 8000,
              iconTheme: { primary: '#ef4444', secondary: '#111827' },
            },
          }}
        />
        <Routes>
          {/* Public routes with Navbar */}
          <Route path="/" element={<><Navbar /><HomePage /></>} />
          <Route path="/login" element={<><Navbar /><LoginPage /></>} />
          <Route path="/register" element={<><Navbar /><RegisterPage /></>} />
          <Route path="/pricing" element={<><Navbar /><PricingPage /></>} />
          <Route path="/features" element={<><Navbar /><FeaturesPage /></>} />
          <Route path="/terms" element={<><Navbar /><TermsPage /></>} />
          <Route path="/privacy" element={<><Navbar /><PrivacyPage /></>} />
          <Route path="/success" element={<><Navbar /><SuccessPage /></>} />
          <Route path="/cancel" element={<><Navbar /><CancelPage /></>} />

          {/* Order requires login */}
          <Route path="/order/:slug" element={<ProtectedRoute><Navbar /><OrderPage /></ProtectedRoute>} />
          <Route path="/console/:id" element={<ProtectedRoute><ConsolePage /></ProtectedRoute>} />
          <Route path="/manage/:id" element={<ProtectedRoute><Navbar /><ServerManagePage /></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute><Navbar /><TicketsPage /></ProtectedRoute>} />
          <Route path="/tickets/:id" element={<ProtectedRoute><Navbar /><TicketDetailPage /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Navbar /><BillingPage /></ProtectedRoute>} />
          <Route path="/balance" element={<ProtectedRoute><Navbar /><BalancePage /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Navbar /><AccountPage /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<><Navbar /><ForgotPasswordPage /></>} />
          <Route path="/reset-password" element={<><Navbar /><ResetPasswordPage /></>} />
          <Route path="/verify-email" element={<><Navbar /><VerifyEmailPage /></>} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/docs" element={<ProtectedRoute><Navbar /><DocsPage /></ProtectedRoute>} />

          {/* Protected routes with Navbar */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Navbar />
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute adminOnly>
                <Navbar />
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <ProtectedRoute>
                <Navbar />
                <StaffPage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">
              <div className="text-center">
                <div className="text-6xl font-black text-green-400 mb-4">404</div>
                <p className="text-gray-400 mb-6">This page doesn't exist in any dimension.</p>
                <a href="/" className="bg-green-500 text-gray-950 font-bold px-6 py-3 rounded-xl hover:bg-green-400 transition-colors">
                  Go home
                </a>
              </div>
            </div>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
